/**
 * Phase 4c — Panneau "Points à vérifier".
 *
 * Affiche la checklist du chantier_type associé au devis avec 3 actions par item :
 * - ✓ Vérifiée (aucune note requise, mais possible)
 * - + Ajouter poste (crée une ligne libre dans le devis, note = titre)
 * - ✗ Non concerné (note recommandée pour justifier l'omission)
 *
 * Le panneau est masqué si :
 * - Le chantier n'a pas de chantier_type_id
 * - Le chantier_type existe mais n'a pas de checklist_items
 *
 * Progression visible (X/Y traités). Items non traités surlignables via `highlightMissing`
 * (piloté par le parent : le blocage envoi renvoie missing_item_ids qu'on met en évidence).
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, PlusCircle, XCircle, ClipboardCheck } from "lucide-react";

const RESPONSE_LABELS = {
  verified: { text: "Vérifiée", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300", Icon: CheckCircle2 },
  added_line: { text: "Poste ajouté", cls: "border-sky-500/40 bg-sky-500/10 text-sky-300", Icon: PlusCircle },
  not_applicable: { text: "Non concerné", cls: "border-slate-500/40 bg-slate-500/10 text-slate-300", Icon: XCircle },
};

export default function ChecklistPanel({
  quote,
  setQuote,
  isDraft,
  chantierTypeId,
  highlightMissing = [],
  onAddLine,
}) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState({}); // itemId → true durant PUT
  const [openNote, setOpenNote] = useState(null); // itemId → {itemId, mode: 'not_applicable'} pour saisir la note

  useEffect(() => {
    if (!chantierTypeId) {
      setItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/chantier-types/${chantierTypeId}`);
        if (!cancelled) setItems(data?.checklist_items || []);
      } catch (e) {
        if (!cancelled) setItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [chantierTypeId]);

  const responses = quote?.checklist_responses || [];
  const responseByItem = useMemo(
    () => Object.fromEntries(responses.map((r) => [r.item_id, r])),
    [responses],
  );

  if (!chantierTypeId || items === null) return null;
  if (items.length === 0) return null;

  const treatedCount = items.filter((it) => responseByItem[it.id]).length;
  const total = items.length;

  const put = async (itemId, payload) => {
    setBusy((b) => ({ ...b, [itemId]: true }));
    try {
      const { data } = await api.put(`/quotes/${quote.id}/checklist/${itemId}`, payload);
      setQuote(data);
      toast.success("Point mis à jour");
      setOpenNote(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [itemId]: false }));
    }
  };

  const clear = async (itemId) => {
    setBusy((b) => ({ ...b, [itemId]: true }));
    try {
      const { data } = await api.delete(`/quotes/${quote.id}/checklist/${itemId}`);
      setQuote(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy((b) => ({ ...b, [itemId]: false }));
    }
  };

  const handleAddLine = async (item) => {
    // Délègue au parent pour créer la ligne + attendre son id + PUT checklist added_line_id
    if (!onAddLine) return;
    const lineId = await onAddLine(item);
    if (lineId) {
      await put(item.id, { response: "added_line", added_line_id: lineId, note: `Ajouté depuis checklist : ${item.label}` });
    }
  };

  return (
    <Card
      className={`border-slate-800 bg-slate-900/40 ${
        highlightMissing.length > 0 ? "ring-2 ring-red-500/40" : ""
      }`}
      data-testid="checklist-panel"
    >
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-slate-400" />
            <h3 className="text-xs uppercase tracking-wider text-slate-400">Points à vérifier</h3>
          </div>
          <Badge
            className={`tabular ${
              treatedCount === total
                ? "border-emerald-500/40 bg-emerald-500/15 text-emerald-300"
                : "border-slate-700 bg-slate-800 text-slate-300"
            }`}
            data-testid="checklist-progress"
          >
            {treatedCount} / {total}
          </Badge>
        </div>

        <ul className="space-y-2">
          {items.map((it) => {
            const resp = responseByItem[it.id];
            const isMissing = highlightMissing.includes(it.id);
            const isNoting = openNote?.itemId === it.id;
            const meta = resp ? RESPONSE_LABELS[resp.response] : null;
            return (
              <li
                key={it.id}
                className={`rounded border p-2.5 text-xs transition ${
                  isMissing
                    ? "border-red-500/60 bg-red-500/5"
                    : resp
                      ? "border-slate-800 bg-slate-950/40"
                      : "border-slate-700 bg-slate-900/60"
                }`}
                data-testid={`checklist-item-${it.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-100">{it.label}</div>
                    {it.description && (
                      <div className="mt-0.5 text-[10px] text-slate-500">{it.description}</div>
                    )}
                    {resp && meta && (
                      <div className={`mt-1.5 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] ${meta.cls}`}>
                        <meta.Icon className="h-2.5 w-2.5" /> {meta.text}
                        {resp.note ? ` — ${resp.note}` : ""}
                      </div>
                    )}
                  </div>
                  {resp && isDraft && (
                    <button
                      onClick={() => clear(it.id)}
                      className="text-[10px] text-slate-500 underline-offset-2 hover:text-slate-200 hover:underline"
                      data-testid={`checklist-clear-${it.id}`}
                    >
                      annuler
                    </button>
                  )}
                </div>

                {isDraft && !resp && !isNoting && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => put(it.id, { response: "verified", note: "" })}
                      disabled={busy[it.id]}
                      className="border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 h-7 text-[11px] px-2"
                      data-testid={`checklist-verify-${it.id}`}
                    >
                      <CheckCircle2 className="mr-1 h-3 w-3" /> Vérifiée
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAddLine(it)}
                      disabled={busy[it.id]}
                      className="border-sky-500/40 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20 h-7 text-[11px] px-2"
                      data-testid={`checklist-addline-${it.id}`}
                    >
                      <PlusCircle className="mr-1 h-3 w-3" /> Ajouter poste
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setOpenNote({ itemId: it.id, mode: "not_applicable" })}
                      disabled={busy[it.id]}
                      className="border-slate-600 bg-slate-800/60 text-slate-300 hover:bg-slate-800 h-7 text-[11px] px-2"
                      data-testid={`checklist-notapplicable-${it.id}`}
                    >
                      <XCircle className="mr-1 h-3 w-3" /> Non concerné
                    </Button>
                  </div>
                )}

                {isNoting && (
                  <NoteEditor
                    onCancel={() => setOpenNote(null)}
                    onConfirm={(note) =>
                      put(it.id, { response: openNote.mode, note })
                    }
                    testId={`checklist-note-editor-${it.id}`}
                  />
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

function NoteEditor({ onCancel, onConfirm, testId }) {
  const [note, setNote] = useState("");
  return (
    <div className="mt-2 space-y-1.5" data-testid={testId}>
      <Textarea
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Justification (facultative) : pourquoi ce point n'est pas concerné…"
        className="text-[11px] border-slate-700 bg-slate-950/40 text-slate-100"
        data-testid={`${testId}-input`}
      />
      <div className="flex justify-end gap-1.5">
        <Button
          size="sm"
          variant="outline"
          onClick={onCancel}
          className="h-6 border-slate-700 bg-slate-800/60 text-[10px] text-slate-300"
        >
          Annuler
        </Button>
        <Button
          size="sm"
          onClick={() => onConfirm(note.trim())}
          className="h-6 bg-[#C4553B] text-[10px] text-white hover:bg-[#A8402C]"
          data-testid={`${testId}-confirm`}
        >
          Valider
        </Button>
      </div>
    </div>
  );
}
